/* eslint-disable react/forbid-component-props */
import clsx from 'clsx'
import React, { useEffect, useRef, useState } from 'react'
import type { PropsWithChildren } from 'react'
import { Heading, ModalContext, useSlottedContext } from 'react-aria-components'
import { useIntl } from 'react-intl'
import IconButton from '../IconButton/IconButton'
import Close from '../icons/Close'

function getScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement
  while (parent) {
    const { overflow, overflowY } = window.getComputedStyle(parent)
    if (
      ['auto', 'scroll'].includes(overflow) ||
      ['auto', 'scroll'].includes(overflowY)
    )
      return parent
    parent = parent.parentElement
  }
  return null
}

function Header({
  children,
}: PropsWithChildren<Record<string, unknown>>): JSX.Element {
  const modalContext = useSlottedContext(ModalContext)

  const onOpenChange = modalContext?.onOpenChange
  const isDismissable = modalContext?.isDismissable

  const intl = useIntl()

  const headerRef = useRef<HTMLDivElement>(null)
  const [hasScrolled, setHasScrolled] = useState(false)

  useEffect(() => {
    const scrollParent = headerRef.current && getScrollParent(headerRef.current)

    if (!scrollParent) {
      return
    }

    const handleScroll = () => setHasScrolled(scrollParent.scrollTop > 0)
    scrollParent.addEventListener('scroll', handleScroll)

    return () => scrollParent.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div
      className={clsx(
        'flex items-center justify-between p-4 pl-6 shrink-0 sticky top-0 bg-white-100 z-9999 transition-shadow duration-200',
        hasScrolled && 'shadow-3',
      )}
      ref={headerRef}
    >
      <Heading slot="title">{children}</Heading>
      {isDismissable ? (
        <IconButton
          aria-label={intl.formatMessage({ id: 'Ui.close' })}
          onClick={() => onOpenChange?.(false)}
          size="base"
        >
          <Close />
        </IconButton>
      ) : null}
    </div>
  )
}

export default Header
